import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, CreditCardIcon, FileTextIcon, LoaderCircleIcon, LockIcon, XIcon } from 'lucide-react';
import { bill, savedCard } from '../../data/eau';
interface WaterPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onPaid: () => void;
}
type Stage = 'carte' | 'traitement' | 'paye';
const secondaryButton = 'inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const primaryButton = 'inline-flex items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
export function WaterPaymentDialog({
  open,
  onClose,
  onPaid
}: WaterPaymentDialogProps) {
  const [stage, setStage] = useState<Stage>('carte');
  const [useSaved, setUseSaved] = useState(true);
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);
  useEffect(() => {
    if (!open) {
      setStage('carte');
      setUseSaved(true);
    }
  }, [open]);
  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);
  const pay = () => {
    setStage('traitement');
    timerRef.current = window.setTimeout(() => {
      setStage('paye');
      onPaid();
    }, 1400);
  };
  return <AnimatePresence data-id="element-1298" data-mp="akgxv21pf9qi">
      {open && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1299" data-mp="3s29u85vbdj8x">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/45" data-id="element-1300" data-mp="3n2f3c2bjgtnv" />

          <motion.div role="dialog" aria-modal="true" aria-label="Paiement de la facture d’eau" initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-1301" data-mp="393p5gkgwapyg">
            <div className="flex items-start justify-between gap-4" data-id="element-1302" data-mp="2rucvo5yxub11">
              <div data-id="element-1303" data-mp="36iherb1wkokc">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-1304" data-mp="36izg2m3f9sz4">
                  Facture d’eau {bill.reference}
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1305" data-mp="31pwo2ieamrl1">
                  {stage === 'paye' ? 'Paiement reçu' : `${bill.amount} à payer`}
                </h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1306" data-mp="3phbr6ddw9cxc">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1307" data-mp="h79kpxif7ok7" />
              </button>
            </div>

            <div className="mt-5" data-id="element-1308" data-mp="m6drkhhvha7d">
              {stage === 'carte' && <div data-id="element-1309" data-mp="m8gsvxoh6pre">
                  <dl className="rounded-2xl border border-slate-200 bg-ville-sand p-4 text-sm" data-id="element-1310" data-mp="r5gx031rlifv">
                    <div className="flex items-start justify-between gap-4" data-id="element-1311" data-mp="v724t9zfz6j7">
                      <div data-id="element-1312" data-mp="v4y3mzsxglmm">
                        <dt className="sr-only" data-id="element-1313" data-mp="3umfo2x3mi73u">Période</dt>
                        <dd className="font-extrabold text-ville-navy" data-id="element-1314" data-mp="3uo0h2iaahhzl">{bill.period}</dd>
                        <dd className="mt-0.5 text-slate-600" data-id="element-1315" data-mp="3ge582df297n">
                          {bill.consumption} · échéance le {bill.dueOn}
                        </dd>
                      </div>
                      <div className="text-right" data-id="element-1316" data-mp="c8zl9apny1ss">
                        <dt className="sr-only" data-id="element-1317" data-mp="3bvxwivuj1hk2">Montant</dt>
                        <dd className="text-lg font-extrabold text-ville-navy" data-id="element-1318" data-mp="3bvfshd1mhz1l">{bill.amount}</dd>
                        <dd className="text-xs text-slate-500" data-id="element-1319" data-mp="3fvghfrmclsmw">{bill.reference}</dd>
                      </div>
                    </div>
                  </dl>

                  <fieldset className="mt-5" data-id="element-1320" data-mp="33efgx6l0ubup">
                    <legend className="text-[15px] font-bold text-ville-navy" data-id="element-1321" data-mp="33fgvvm55rihp">Moyen de paiement</legend>
                    <label className={`mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors duration-150 ease-ville ${useSaved ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200'}`} data-id="element-1322" data-mp="2zf6oc51pwk0q">
                      <input type="radio" name="moyen-paiement-eau" checked={useSaved} onChange={() => setUseSaved(true)} className="h-4 w-4 accent-ville-navy" data-id="element-1323" data-mp="2fp2z5ibmovgs" />
                      <CreditCardIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-1324" data-mp="21plglkoxeefv" />
                      <span className="min-w-0 flex-1" data-id="element-1325" data-mp="1s1zc0agxwr3q">
                        <span className="block text-sm font-bold text-ville-navy" data-id="element-1326" data-mp="1s0ejeqe6ri8i">
                          {savedCard.brand} •••• {savedCard.last4}
                        </span>
                        <span className="block text-xs text-slate-500" data-id="element-1327" data-mp="1i4tk500i4w2g">Expire {savedCard.expiry}</span>
                      </span>
                    </label>

                    <label className={`mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors duration-150 ease-ville ${useSaved ? 'border-slate-200' : 'border-ville-indigo bg-ville-indigo-soft'}`} data-id="element-1328" data-mp="2ash54g5opopm">
                      <input type="radio" name="moyen-paiement-eau" checked={!useSaved} onChange={() => setUseSaved(false)} className="h-4 w-4 accent-ville-navy" data-id="element-1329" data-mp="260pds3epfeyf" />
                      <span className="text-sm font-bold text-ville-navy" data-id="element-1330" data-mp="n1rfhd9dw75x">Utiliser une autre carte</span>
                    </label>
                  </fieldset>

                  {!useSaved && <div className="mt-4 space-y-3" data-id="element-1331" data-mp="wrkpwaxsig8l">
                      <div data-id="element-1332" data-mp="wufca80d6b4n">
                        <label htmlFor="eau-carte-numero" className="block text-xs font-bold text-slate-500" data-id="element-1333" data-mp="rszqgdqd40l7">
                          Numéro de carte
                        </label>
                        <input id="eau-carte-numero" inputMode="numeric" placeholder="0000 0000 0000 0000" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-1334" data-mp="3vahvkder2thx" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-1335" data-mp="1kh5a0x5ojjjg">
                        <div data-id="element-1336" data-mp="3e0xdkkbm7xfn">
                          <label htmlFor="eau-carte-exp" className="block text-xs font-bold text-slate-500" data-id="element-1337" data-mp="3e1ysizvr542n">
                            Expiration
                          </label>
                          <input id="eau-carte-exp" placeholder="MM / AA" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-1338" data-mp="3nyjwdhw1c94w" />
                        </div>
                        <div data-id="element-1339" data-mp="ak74txonf6a9">
                          <label htmlFor="eau-carte-cvc" className="block text-xs font-bold text-slate-500" data-id="element-1340" data-mp="ajxeua3lsz4k">
                            Code de sécurité
                          </label>
                          <input id="eau-carte-cvc" inputMode="numeric" placeholder="123" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-1341" data-mp="2cc3dmzjfm21c" />
                        </div>
                      </div>
                    </div>}

                  <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-1342" data-mp="konkapy7uldj">
                    <LockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1343" data-mp="1he4o0zdn1xcl" />
                    Paiement traité par la passerelle sécurisée de la Municipalité de Baie-Trinité.
                  </p>

                  <button type="button" onClick={pay} className={`mt-6 w-full ${primaryButton}`} data-id="element-1344" data-mp="18d6sz6l6h065">
                    Payer {bill.amount}
                  </button>
                </div>}

              {stage === 'traitement' && <div className="py-10 text-center" data-id="element-1345" data-mp="3t5iab4tx0ba7">
                  <LoaderCircleIcon className="mx-auto h-8 w-8 animate-spin text-ville-indigo" aria-hidden="true" data-id="element-1346" data-mp="3o4b61fqzdu8a" />
                  <p className="mt-4 text-sm font-semibold text-ville-navy" role="status" data-id="element-1347" data-mp="nrjih6l7qtbn">
                    Traitement du paiement…
                  </p>
                </div>}

              {stage === 'paye' && <div data-id="element-1348" data-mp="xjgxkofce5fn">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1349" data-mp="xjz5wjfa4a3x">
                    <CheckIcon className="h-6 w-6" data-id="element-1350" data-mp="16knid9p0y2ep" />
                  </span>
                  <h3 className="mt-3 text-lg font-extrabold text-ville-navy" data-id="element-1351" data-mp="11j7znoabk7zh">Facture d’eau payée</h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-1352" data-mp="1gg8p1l51fqus">
                    {bill.amount} réglé sur {savedCard.brand} •••• {savedCard.last4}. Le reçu est déposé dans
                    votre profil.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3" data-id="element-1353" data-mp="3d9a78yvm3tr7">
                    <Link to="/mon-profil" className={secondaryButton} data-id="element-1354" data-mp="3dav3qu1hlmrp">
                      <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-1355" data-mp="1sqzr7f5eyjtq" />
                      Voir mon reçu
                    </Link>
                  </div>
                  <button type="button" onClick={onClose} className={`mt-4 w-full ${primaryButton}`} data-id="element-1356" data-mp="1j1g5m1qbgbv6">
                    Terminer
                  </button>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}